import { useState, useEffect, useCallback } from 'react';
import { Scale, Plus, Download, Trash2, X, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { authFetch, downloadFile } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_LABELS = { NDA: 'Non-Disclosure Agreement', MSA: 'Master Service Agreement', IO: 'Insertion Order', PO: 'Purchase Order' };
const DOC_COLORS = { NDA: 'bg-blue-100 text-blue-700', MSA: 'bg-purple-100 text-purple-700', IO: 'bg-amber-100 text-amber-700', PO: 'bg-emerald-100 text-emerald-700' };

const LegalDocsView = ({ user }) => {
  const [docs, setDocs] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [clients, setClients] = useState([]);
  const [docType, setDocType] = useState('NDA');
  const [form, setForm] = useState({ client_name: '', notes: '', fields: {} });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, tplRes, driveRes] = await Promise.all([
        authFetch(`${API}/api/legal`),
        authFetch(`${API}/api/legal/templates`),
        // /api/drive/clients is scoped by the user's `assigned_folders` (Admin sees all)
        authFetch(`${API}/api/drive/clients`),
      ]);
      if (docsRes.ok) setDocs((await docsRes.json()).legal_docs || []);
      if (tplRes.ok) setTemplates((await tplRes.json()).templates || {});
      if (driveRes.ok) {
        const data = await driveRes.json();
        setClients((data.clients || []).map(c => ({
          id: c.drive_folder_id, name: c.folder_name, drive_folder_id: c.drive_folder_id,
        })));
      }
    } catch (e) { console.error('Failed to load legal docs:', e); toast.error('Failed to load data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => setForm({ client_name: '', notes: '', fields: {} });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name) { toast.error('Client name required'); return; }

    const res = await authFetch(`${API}/api/legal`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_name: form.client_name, doc_type: docType, fields: form.fields, notes: form.notes })
    });
    if (res.ok) { toast.success(`${docType} created`); setShowForm(false); resetForm(); fetchData(); }
    else toast.error('Failed to create document');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    const res = await authFetch(`${API}/api/legal/${id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Document deleted'); setSelectedDoc(null); fetchData(); }
  };

  const exportPdf = async (id) => {
    try {
      await downloadFile(`/api/legal/${id}/export-pdf`, `Legal_${id.slice(0,8)}.pdf`);
    } catch (e) { toast.error('PDF export failed'); }
  };

  const viewDoc = async (id) => {
    try {
      const res = await authFetch(`${API}/api/legal/${id}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedDoc(data.legal_doc);
      }
    } catch (e) { toast.error('Failed to load document'); }
  };

  const updateField = (key, val) => setForm(f => ({ ...f, fields: { ...f.fields, [key]: val } }));

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-brand" size={28} /></div>;

  // Detail view
  if (selectedDoc) {
    const doc = selectedDoc;
    const hasTemplate = doc.template_source === 'google_drive' || doc.drive_template_html;
    const renderedHtml = doc.generated_html || '';

    return (
      <div data-testid="legal-detail">
        <button onClick={() => setSelectedDoc(null)} className="flex items-center gap-1 text-sm text-brand hover:underline mb-4">&larr; Back</button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${DOC_COLORS[doc.doc_type] || 'bg-surface-page text-text-secondary'}`}>{doc.doc_type}</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${doc.status === 'Draft' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{doc.status}</span>
              {doc.template_source === 'google_drive' && <span className="text-xs font-medium text-brand bg-brand/10 px-2 py-0.5 rounded-full">Drive Template</span>}
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-text-primary">{DOC_LABELS[doc.doc_type] || doc.doc_type}</h2>
            <p className="text-sm text-text-tertiary">{doc.client_name} &middot; {doc.id?.slice(0,8)}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => exportPdf(doc.id)} data-testid="legal-export-pdf"><Download size={14} className="mr-1" /> PDF</Button>
            <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(doc.id)}><Trash2 size={14} /></Button>
          </div>
        </div>

        {/* Render the full template document */}
        {renderedHtml && (
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">
                {hasTemplate ? 'Document (from Google Drive Template)' : 'Document Preview'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg bg-white overflow-auto max-h-[70vh]" data-testid="legal-template-preview">
                <iframe
                  srcDoc={renderedHtml}
                  title="Legal Doc Preview"
                  className="w-full border-0"
                  style={{ minHeight: '500px', height: '70vh' }}
                  sandbox="allow-same-origin"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Field summary */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Fields</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(doc.fields || {}).map(([k, v]) => (
                <div key={k} className="flex border-b border-border-subtle pb-2">
                  <span className="w-44 text-sm font-medium text-text-tertiary capitalize">{k.replace(/_/g, ' ')}</span>
                  <span className="text-sm text-text-primary flex-1">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Template-specific form fields
  const renderTemplateFields = () => {
    const tpl = templates[docType];
    if (!tpl) return null;

    return (tpl.fields || []).map(field => {
      if (field === 'items') {
        return (
          <div key={field}>
            <Label className="capitalize">{field.replace(/_/g, ' ')}</Label>
            <p className="text-xs text-text-muted mt-0.5">Add items as JSON array (advanced)</p>
            <textarea value={form.fields[field] || ''} onChange={e => {
              try { updateField(field, JSON.parse(e.target.value)); } catch (e) { updateField(field, e.target.value); }
            }} className="w-full mt-1 px-3 py-2 border border-input rounded-xl bg-white text-sm min-h-[60px]" placeholder='[{"description":"Item","quantity":1,"unit_price":100}]' />
          </div>
        );
      }
      return (
        <div key={field}>
          <Label className="capitalize">{field.replace(/_/g, ' ')}</Label>
          <Input value={form.fields[field] || ''} onChange={e => updateField(field, e.target.value)} className="mt-1" placeholder={field.replace(/_/g, ' ')} data-testid={`legal-field-${field}`} />
        </div>
      );
    });
  };

  return (
    <div data-testid="legal-docs-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-semibold tracking-tight text-text-primary">Legal Documents</h1><p className="text-sm text-text-tertiary">NDA, MSA, IO, PO templates</p></div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-brand hover:bg-brand-hover" data-testid="create-legal-btn"><Plus size={16} className="mr-1" /> New Document</Button>
      </div>

      {docs.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Scale className="mx-auto mb-3 text-text-muted" size={40} /><p className="text-text-tertiary">No legal documents yet</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map(doc => (
            <Card key={doc.id} className="cursor-pointer hover:border-brand/30 transition-colors" onClick={() => viewDoc(doc.id)} data-testid={`legal-card-${doc.id?.slice(0,8)}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${DOC_COLORS[doc.doc_type] || 'bg-surface-page text-text-secondary'}`}>{doc.doc_type}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${doc.status === 'Draft' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{doc.status}</span>
                </div>
                <h3 className="text-base font-bold text-text-primary mt-2">{DOC_LABELS[doc.doc_type] || doc.doc_type}</h3>
                <p className="text-sm text-text-tertiary mt-1">{doc.client_name}</p>
                <p className="text-xs text-text-muted mt-2">{doc.created_at?.slice(0, 10)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-text-primary">New Legal Document</h2>
              <button onClick={() => setShowForm(false)} className="p-1 rounded-lg hover:bg-surface-page"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client</Label>
                  <select value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white" data-testid="legal-client-select" required>
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Document Type</Label>
                  <div className="flex gap-2 mt-1.5 flex-wrap">
                    {Object.keys(DOC_LABELS).map(t => (
                      <button key={t} type="button" onClick={() => { setDocType(t); setForm(f => ({ ...f, fields: {} })); }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${docType === t ? 'bg-brand text-white' : 'bg-surface-page text-text-secondary hover:bg-surface-page'}`}
                        data-testid={`legal-type-${t}`}
                      >{t}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">{renderTemplateFields()}</div>

              <div><Label>Notes</Label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white min-h-[60px] text-sm" /></div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" className="bg-brand hover:bg-brand-hover" data-testid="legal-submit">Create {docType}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalDocsView;
